import { Component, DestroyRef, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { finalize } from 'rxjs';
import _ from 'lodash';
import {
  FundingBankAccount,
  FundMovement,
  FundMovementStatus,
  FundMovementType,
  FundMovementUpdate,
} from '../../../models/fund-movement/fund-movement';
import { FundOpeningFormModel, fundOpeningFieldLabels } from '../../../models/fund-movement/fund-movement-form';
import { PettyCashFund } from '../../../models/petty-cash-fund/petty-cash-fund';
import { BankChargesOption } from '../../shared/bank-charges-section/bank-charges-section.component';
import { FundMovementProxyService } from '../../../proxies/fund-movement.proxy.service';
import { currencyLabel, currencySymbol } from '../../../utilities/currency.util';
import { reportInvalidFormFields } from '../../../utilities/form-error-reporter.util';
import { ControlsOf } from '../../../utilities/form.types';

/**
 * The fund's opening movement (#5786). On a fund ready for funding the treasurer fills it in — amount (the fund's
 * maximum by default), date, bank and charges — saves it as Draft and posts it, which books the entry, records the
 * bank movement and makes the fund Active. No approval. On an Active fund it shows the posted opening.
 */
@Component({
  selector: 'app-fund-opening',
  templateUrl: './fund-opening.component.html',
})
export class FundOpeningComponent implements OnInit {
  private readonly proxy = inject(FundMovementProxyService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly fund = input.required<PettyCashFund>();
  /** PettyCashTreasurer: may save, post and delete. Everyone else reads. */
  readonly canFund = input(false);
  readonly funded = output<void>();

  readonly fieldLabels = fundOpeningFieldLabels;
  readonly isLoading = signal(0);
  readonly opening = signal<FundMovement | null>(null);
  readonly banks = signal<FundingBankAccount[]>([]);

  readonly form = new FormGroup<ControlsOf<FundOpeningFormModel>>({
    amount: new FormControl(0, { nonNullable: true, validators: [Validators.required, Validators.min(0.01)] }),
    movementDate: new FormControl<Date | null>(new Date(), Validators.required),
    bankAccount: new FormControl<string | null>(null),
    bankingReference: new FormControl<string | null>(null),
    bankingFee: new FormControl<number | null>(0, Validators.min(0)),
    securityFee: new FormControl<number | null>(0, Validators.min(0)),
    note: new FormControl<string | null>(null),
  });

  /** Mirrors the form for the computed summary, including loads made with emitEvent: false. */
  private readonly formValue = signal(this.form.getRawValue());

  readonly isPosted = computed(() => this.opening()?.status === FundMovementStatus.Posted);
  readonly isEditable = computed(() => this.canFund() && !this.isPosted());
  readonly bankOptions = computed<BankChargesOption[]>(() =>
    this.banks().map((bank) => ({
      label: [bank.code, bank.bankName, bank.name, `****${bank.bankAccountNumber.slice(-4)}`].filter((s) => !!s).join(' · '),
      value: bank.reference,
    })),
  );
  readonly totalCashOut = computed(() => {
    const value = this.formValue();
    return Math.round((value.amount + (value.bankingFee ?? 0) + (value.securityFee ?? 0)) * 100) / 100;
  });
  readonly postedBankLabel = computed(() => {
    const reference = this.opening()?.bankAccountReference;
    return this.bankOptions().find((o) => o.value === reference)?.label ?? this.opening()?.bankGlAccount?.number ?? '';
  });

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.formValue.set(this.form.getRawValue()));
  }

  ngOnInit(): void {
    const fund = this.fund();
    // The API's own rule (amount ≤ the fund's maximum), which depends on the record.
    this.form.controls.amount.addValidators(Validators.max(fund.maxRevolvingBalance));
    this.form.controls.amount.setValue(fund.maxRevolvingBalance);
    this.loadOpening();
    this.loadBanks();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private loadOpening(): void {
    this.startLoading();
    this.proxy
      .getByFund(this.fund().id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        const opening = (response.data ?? []).find((m) => m.type === FundMovementType.Opening) ?? null;
        this.setOpening(opening);
      });
  }

  private loadBanks(): void {
    this.proxy.getFundingBankAccounts(this.fund().id).subscribe((response) => this.banks.set(response.data ?? []));
  }

  private setOpening(opening: FundMovement | null): void {
    this.opening.set(opening);
    if (!_.isNil(opening)) {
      this.form.patchValue(
        {
          amount: opening.amount,
          movementDate: this.fromApiDate(opening.movementDate),
          bankAccount: opening.bankAccountReference,
          bankingReference: opening.bankingReference,
          bankingFee: opening.transferFee,
          securityFee: opening.securityTax,
          note: opening.note,
        },
        { emitEvent: false },
      );
    }
    if (this.isEditable()) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
    this.form.markAsPristine();
    this.formValue.set(this.form.getRawValue());
  }

  save(): void {
    if (!this.validate(false)) return;
    this.startLoading();
    this.proxy
      .saveOpening(this.fund().id, this.payload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (_.isNil(response.data)) return;
        this.setOpening(response.data);
        GlobalMessageUtil.success(this.title(), this.label('FundOpening_Saved', 'Opening saved as draft.'));
      });
  }

  /** Saves first so the posted figures are exactly what is on screen, then posts. */
  confirmPost(): void {
    if (!this.validate(true)) return;
    const fund = this.fund();
    this.confirmationService.confirm({
      header: this.label('FundOpening_Post_Title', 'Post opening'),
      message: this.label(
        'FundOpening_Post_Message',
        'Post {amount}? This books the entry and the bank movement and makes the fund Active. It cannot be undone here.',
      ).replace('{amount}', `${currencySymbol(fund.currency)} ${this.totalCashOut().toFixed(2)}`),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('FundOpening_Post', 'Post'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => this.post(),
    });
  }

  private post(): void {
    this.startLoading();
    this.proxy
      .saveOpening(this.fund().id, this.payload())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((saved) => {
        if (_.isNil(saved.data)) return;
        this.setOpening(saved.data);
        this.startLoading();
        this.proxy
          .post(saved.data.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe((response) => {
            if (_.isNil(response.data)) return;
            this.setOpening(response.data);
            GlobalMessageUtil.success(this.title(), this.label('FundOpening_Posted', 'Opening posted; the fund is now Active.'));
            this.funded.emit();
          });
      });
  }

  confirmDelete(): void {
    const opening = this.opening();
    if (_.isNil(opening)) return;
    this.confirmationService.confirm({
      header: this.label('FundOpening_Delete_Title', 'Delete draft'),
      message: this.label('FundOpening_Delete_Message', 'Delete this draft opening and its attachments?'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.label('Delete', 'Delete'),
      rejectLabel: this.label('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this.startLoading();
        this.proxy
          .delete(opening.id)
          .pipe(finalize(() => this.stopLoading()))
          .subscribe(() => {
            this.opening.set(null);
            this.form.reset({ amount: this.fund().maxRevolvingBalance, movementDate: new Date(), bankingFee: 0, securityFee: 0 });
            GlobalMessageUtil.success(this.title(), this.label('FundOpening_Deleted', 'Draft deleted.'));
          });
      },
    });
  }

  onFile(file: File): void {
    const opening = this.opening();
    if (_.isNil(opening)) return;
    this.startLoading();
    this.proxy
      .addAttachment(opening.id, file)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        if (!_.isNil(response.data)) this.opening.set(response.data);
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  currencyLabel(): string {
    return currencyLabel(this.fund().currency);
  }

  symbol(): string {
    return currencySymbol(this.fund().currency);
  }

  /** Bank is only required to post; amount and date always. */
  private validate(forPost: boolean): boolean {
    const bank = this.form.controls.bankAccount;
    bank.setValidators(forPost ? Validators.required : null);
    bank.updateValueAndValidity({ emitEvent: false });
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    return true;
  }

  private payload(): FundMovementUpdate {
    const value = this.form.getRawValue();
    return {
      amount: value.amount,
      movementDate: this.toApiDate(value.movementDate ?? new Date()),
      bankAccountReference: value.bankAccount,
      bankingReference: value.bankingReference?.trim() || null,
      transferFee: value.bankingFee ?? 0,
      securityTax: value.securityFee ?? 0,
      note: value.note?.trim() || null,
    };
  }

  /** yyyy-MM-dd of the picked local day, so the server stores that calendar day. */
  private toApiDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /** The stored calendar day as a local Date (the server keeps midnight UTC). */
  private fromApiDate(value: string | null): Date | null {
    if (_.isNil(value)) return null;
    const [year, month, day] = value.substring(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private title(): string {
    return this.label('FundOpening_Title', 'Fund opening');
  }

  private label(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }
}
